import { format, isSaturday } from 'date-fns';
import React, { useState } from 'react';

import { Icon } from '../../../../common/Icon';
import { Tooltip } from '../../../../common/Tooltip';
import { TextLink } from '../../../../components/TextLink';
import {
  GroupedAdhocData,
  GroupedMeetingData,
} from '../../hooks/useSprintTimeline';

import { getTotalGroupedItems, getTotalMeetingDuration } from './helpers';
import { TimelineCell } from './TimelineCell';
import { TProgressData } from './types';

interface TimelineGridProps {
  workingDays: Date[];
  tickets: Array<{
    id: string;
    ticket_number: string;
    title: string;
    external_link: string;
  }>;
  progressData: Record<string, TProgressData>;
  dayStatus: Record<
    string,
    { type: 'OOO' | 'MISSED' | 'NORMAL'; reason?: string }
  >;
  adhocs: GroupedAdhocData;
  meetings: GroupedMeetingData;
}

function getBorderClasses(
  hoveredColumn: number | null,
  colIndex: number,
): string {
  // First column with both borders
  if (colIndex === 0) {
    if (hoveredColumn === 0) {
      return 'border-0';
    }
    if (hoveredColumn === 1) {
      return 'border-l-2 border-dashed border-[#D3D3D3]';
    }
    return 'border-l-2 border-r-2 border-dashed border-[#D3D3D3]';
  }
  // No border on hovered column
  if (hoveredColumn === colIndex) {
    return 'border-r-0';
  }
  // No border on column before hovered column
  if (hoveredColumn === colIndex + 1) {
    return 'border-r-0';
  }
  // Default dashed border
  return 'border-r-2 border-dashed border-[#D3D3D3]';
}

export function TimelineGrid({
  workingDays,
  tickets,
  progressData,
  dayStatus,
  adhocs,
  meetings,
}: TimelineGridProps): JSX.Element {
  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);
  return (
    <div
      className="grid [--min-width:18rem] l:[--min-width:24rem]"
      style={{
        gridTemplateColumns: 'minmax(var(--min-width), var(--min-width)) 1fr',
      }}
    >
      {/* Header and Ticket numbers column */}
      <div className="sticky left-0 bg-white z-30">
        <div
          className="grid"
          style={{ gridTemplateRows: `repeat(${tickets.length + 3}, 56px)` }}
        >
          <div className="relative">
            <div className="relative z-20 h-full pr-6 flex items-center justify-start text-sm font-medium text-gray-500">
              Tickets
            </div>
          </div>
          {tickets.map((ticket) => (
            <div key={ticket.id} className="relative">
              <div className="relative z-20 h-14 pr-6 flex items-center justify-start">
                <Tooltip
                  content={
                    <div className="space-y-2 max-w-60">
                      <div className="text-gray-900">{ticket.title}</div>
                    </div>
                  }
                >
                  <TextLink href={ticket.external_link} target="_blank">
                    <div className="flex items-center gap-1">
                      <span className="truncate w-[15rem] l:w-[20rem]">
                        {ticket.ticket_number}: {ticket.title}
                      </span>
                      <Icon name="link" className="w-4 h-4 flex-shrink-0" />
                    </div>
                  </TextLink>
                </Tooltip>
              </div>
            </div>
          ))}
          <div className="relative">
            <div className="relative z-20 h-14 pr-6 flex items-center justify-start">
              Ad-Hoc Tasks {`(${getTotalGroupedItems(adhocs)})`}
            </div>
          </div>
          <div className="relative">
            <div className="relative z-20 h-14 pr-6 flex items-center justify-start">
              Meetings{getTotalMeetingDuration(meetings)}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline grid with header */}
      <div className="relative overflow-x-auto overflow-y-hidden">
        <div className="min-w-max">
          {/* Header row */}
          <div
            className="grid relative z-10"
            style={{
              gridTemplateColumns: workingDays
                .map((day) =>
                  isSaturday(day) ? 'minmax(48px, 0.5fr)' : 'minmax(96px, 1fr)',
                )
                .join(' '),
            }}
          >
            {workingDays.map((day, colIndex) => {
              let bgClass = '';
              if (hoveredColumn === colIndex) {
                bgClass = 'bg-gray-200';
              } else if (isSaturday(day)) {
                bgClass = 'bg-gradient-to-br from-gray-50 to-gray-100';
              }

              return (
                <div
                  key={day.toISOString()}
                  className={`text-sm text-gray-500 text-center relative ${bgClass} ${
                    isSaturday(day) ? 'p-1' : 'p-4'
                  }`}
                  onMouseEnter={() => setHoveredColumn(colIndex)}
                  onMouseLeave={() => setHoveredColumn(null)}
                  style={
                    isSaturday(day) && hoveredColumn !== colIndex
                      ? {
                          background:
                            'repeating-linear-gradient(135deg, #E1E1E1 0 1px, transparent 3px 12px)',
                        }
                      : {}
                  }
                >
                  <div
                    className={`absolute inset-0 ${getBorderClasses(
                      hoveredColumn,
                      colIndex,
                    )} z-0`}
                  />
                  <div className="relative z-20">
                    {!isSaturday(day) && format(day, 'dd MMM')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Grid container */}
          <div
            className="grid relative z-10"
            style={{
              gridTemplateColumns: workingDays
                .map((day) =>
                  isSaturday(day) ? 'minmax(48px, 0.5fr)' : 'minmax(96px, 1fr)',
                )
                .join(' '),
              gridTemplateRows: `repeat(${
                tickets.length + 2
              }, minmax(48px, 56px))`,
            }}
          >
            {tickets.map((ticket) =>
              workingDays.map((day, colIndex) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const key = `${dateStr}-${ticket.id}`;
                const progressInfo = progressData[key];
                const status = dayStatus[dateStr];
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    key={key}
                    className={`relative ${
                      hoveredColumn === colIndex ? 'bg-gray-200' : ''
                    }`}
                    onMouseEnter={() => setHoveredColumn(colIndex)}
                    onMouseLeave={() => setHoveredColumn(null)}
                  >
                    <div
                      className={`absolute inset-0 ${getBorderClasses(
                        hoveredColumn,
                        colIndex,
                      )} z-30 pointer-events-none`}
                    />
                    <div className="relative z-20">
                      {status?.type === 'NORMAL' && (
                        <TimelineCell
                          progress={progressInfo?.progress}
                          previousProgress={progressInfo?.previousProgress}
                          hasBlocker={progressInfo?.hasBlocker ?? false}
                          freeLeft={
                            colIndex >= 1 &&
                            dayStatus[
                              format(workingDays[colIndex - 1], 'yyyy-MM-dd')
                            ]?.type === 'NORMAL' &&
                            progressData[
                              `${format(
                                workingDays[colIndex - 1],
                                'yyyy-MM-dd',
                              )}-${ticket.id}`
                            ]?.progress !== null
                          }
                          freeRight={
                            colIndex < workingDays.length - 1 &&
                            dayStatus[
                              format(workingDays[colIndex + 1], 'yyyy-MM-dd')
                            ]?.type === 'NORMAL' &&
                            progressData[
                              `${format(
                                workingDays[colIndex + 1],
                                'yyyy-MM-dd',
                              )}-${ticket.id}`
                            ]?.progress !== null
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              }),
            )}
            {workingDays.map((day, colIndex) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const status = dayStatus[dateStr];
              return (
                <div
                  role="button"
                  tabIndex={0}
                  key={dateStr}
                  className={`relative ${
                    hoveredColumn === colIndex ? 'bg-gray-200' : ''
                  }`}
                  onMouseEnter={() => setHoveredColumn(colIndex)}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  <div
                    className={`absolute inset-0 ${getBorderClasses(
                      hoveredColumn,
                      colIndex,
                    )} z-0`}
                  />
                  <div className="relative z-20">
                    {status?.type === 'NORMAL' &&
                      adhocs[dateStr]?.length > 0 && (
                        <TimelineCell
                          type="adhoc"
                          progress={adhocs[dateStr]?.length}
                        />
                      )}
                  </div>
                </div>
              );
            })}
            {workingDays.map((day, colIndex) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const status = dayStatus[dateStr];
              return (
                <div
                  role="button"
                  tabIndex={0}
                  key={dateStr}
                  className={`relative ${
                    hoveredColumn === colIndex ? 'bg-gray-200' : ''
                  }`}
                  onMouseEnter={() => setHoveredColumn(colIndex)}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  <div
                    className={`absolute inset-0 ${getBorderClasses(
                      hoveredColumn,
                      colIndex,
                    )} z-0`}
                  />
                  <div className="relative z-20">
                    {status?.type === 'NORMAL' &&
                      meetings[dateStr]?.length > 0 && (
                        <TimelineCell
                          type="meeting"
                          progress={meetings[dateStr]?.reduce(
                            (acc, curr) => acc + curr.duration,
                            0,
                          )}
                        />
                      )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Status overlay */}
          <div
            className="absolute top-0 left-0 right-0 grid  pointer-events-none"
            style={{
              gridTemplateColumns: workingDays
                .map((day) =>
                  isSaturday(day) ? 'minmax(48px, 0.5fr)' : 'minmax(96px, 1fr)',
                )
                .join(' '),
              height: `${56 * (tickets.length + 3)}px`,
            }}
          >
            {workingDays.map((day, colIndex) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const status = dayStatus[dateStr];
              let statusText = '';
              let statusBackground = isSaturday(day)
                ? 'repeating-linear-gradient(135deg, #E1E1E1 0 1px, transparent 3px 12px)'
                : '';
              if (!isSaturday(day)) {
                statusText = status?.type === 'OOO' ? 'OOO' : 'Missed';
                statusBackground =
                  status?.type === 'OOO'
                    ? 'repeating-linear-gradient(135deg, #D5ECFF 0 1px, transparent 3px 12px)'
                    : 'repeating-linear-gradient(135deg, #FEEADE 0 1px, transparent 3px 12px)';
              }

              return (
                <div key={day.toISOString()} className="relative">
                  {(status?.type !== 'NORMAL' || isSaturday(day)) && (
                    <div
                      className={`absolute inset-0 flex items-center justify-center px-2 ${
                        hoveredColumn === colIndex ? 'bg-gray-200/60' : ''
                      }`}
                      style={{
                        background: statusBackground,
                      }}
                    >
                      {!isSaturday(day) && (
                        <div className="h-7 w-full flex items-center justify-center rounded bg-white z-10">
                          <span className="text-sm font-medium text-gray-400">
                            {statusText}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
